import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  Button,
} from '@mui/material';
import { useTheme } from 'contexts/ThemeContext';
import usePickup from 'hooks/pickups/usePickup';
import {
  CabDeviceTypeGroup,
  cabDeviceTypeGroupsFromRows,
  isCabNonDataBearing,
} from 'lib/cabSection';
import { api } from 'services/api.service';
import { Asset } from 'types/asset';

// Extended Asset interface to include additional fields that might be missing
interface AssetWithExtendedFields extends Asset {
  cpu?: string;
  ram?: string;
  storage?: string;
  grade?: string;
  weight?: number;
  wipeStatus?: string;
  ddStatus?: string;
  eolStatus?: string;
  dataBearing?: string;
}

function assetDdStatus(asset: AssetWithExtendedFields): string {
  return asset.ddStatus ?? asset.wipeStatus ?? '';
}

function assetEolStatus(asset: AssetWithExtendedFields): string {
  return asset.eolStatus ?? asset.status ?? '';
}

export default function CustomerAssetBreakdown() {
  const { theme: customerTheme } = useTheme();
  const { pickup, isLoading: pickupLoading } = usePickup();
  const [assets, setAssets] = useState<AssetWithExtendedFields[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [deviceTypeGroups, setDeviceTypeGroups] = useState<
    Map<string, CabDeviceTypeGroup>
  >(new Map());
  const [deviceTypesLoaded, setDeviceTypesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch assets for this pickup
  useEffect(() => {
    if (pickup?.id) {
      setIsLoadingAssets(true);
      setError(null);

      // Use pickup ID to filter assets
      const params = new URLSearchParams();
      params.append('filter[]', `pickup_id,is,${pickup.id}`);
      params.append('page[size]', '1000'); // Get all assets for this pickup

      api
        .get<{
          assets: AssetWithExtendedFields[];
          meta: { totalPages: number; count: number };
        }>(`/assets?${params.toString()}`)
        .then((response) => {
          setAssets(response.data.assets);
          setIsLoadingAssets(false);
        })
        .catch((err) => {
          console.error('Error fetching assets:', err);
          setError('Failed to load assets');
          setIsLoadingAssets(false);
        });
    }
  }, [pickup?.id]);

  useEffect(() => {
    api
      .get<{
        organizationDeviceTypes?: Array<{
          name?: string;
          group?: string | null;
        }>;
      }>('/organization_device_types')
      .then((response) => {
        setDeviceTypeGroups(
          cabDeviceTypeGroupsFromRows(
            response.data.organizationDeviceTypes ?? []
          )
        );
      })
      .catch(() => {
        setDeviceTypeGroups(new Map());
      })
      .finally(() => {
        setDeviceTypesLoaded(true);
      });
  }, []);

  // Show loading state
  if (pickupLoading || isLoadingAssets || !deviceTypesLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Typography color='error'>{error}</Typography>
      </Box>
    );
  }

  const nonDataBearingAssets = assets.filter((asset) =>
    isCabNonDataBearing(asset.deviceType, asset.dataBearing, deviceTypeGroups)
  );
  const dataBearingItems = assets.filter(
    (asset) =>
      !isCabNonDataBearing(
        asset.deviceType,
        asset.dataBearing,
        deviceTypeGroups
      )
  );

  // Group non-data bearing items by device type
  const nonDataBearingItems = nonDataBearingAssets.reduce(
    (acc: any[], asset) => {
      const existing = acc.find(
        (item) => item.productType === asset.deviceType
      );
      const assetWeight = parseFloat(asset.weight?.toString() || '0') || 0;
      const assetQuantity = parseInt(asset.quantity?.toString() || '1') || 1;

      if (existing) {
        existing.qty += assetQuantity;
        existing.totalWeight += assetWeight;
      } else {
        acc.push({
          productType: asset.deviceType || 'Unknown',
          jobNo: pickup?.orderNumber || '',
          qty: assetQuantity,
          totalWeight: assetWeight,
          jobStatus: pickup?.state || '',
          eolStatus: assetEolStatus(asset),
        });
      }
      return acc;
    },
    []
  );

  // Print to PDF function
  const handlePrintToPDF = () => {
    window.print();
  };

  // Download CSV function
  const handleDownloadCSV = () => {
    // Create CSV content
    const csvContent = generateCSVContent();

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `customer-asset-breakdown-${
        pickup?.orderNumber || pickup?.uuid || 'unknown'
      }-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate CSV content
  const generateCSVContent = () => {
    let csv = '';

    // Add title
    csv += `Customer Asset Breakdown\n`;

    // Data Bearing Items section
    csv += `DATA BEARING ITEMS\n`;
    csv += `Unit ID,Customer Asset Tag,Job No,Product Type,Make,Model,Serial No,CPU,RAM,Storage,Grade,Product Weight (kg),DD Status,Job Status,EOL Status\n`;

    dataBearingItems.forEach((asset) => {
      csv += `"${asset.unitNumber || ''}","${asset.assetTag || ''}","${
        pickup?.orderNumber || ''
      }","${asset.deviceType || ''}","${asset.manufacturer || ''}","${
        asset.model || ''
      }","${asset.serialNumber || ''}","${asset.cpu || ''}","${
        asset.ram || ''
      }","${asset.storage || ''}","${asset.condition || ''}","${
        asset.weight || ''
      }","${assetDdStatus(asset)}","${pickup?.state || ''}","${
        assetEolStatus(asset)
      }"\n`;
    });

    csv += `\n`;

    // Non-Data Bearing Items section
    csv += `NON-DATA BEARING ITEMS\n`;
    csv += `Product Type,Job No,QTY,Total Weight (kg),Job Status,EOL Status\n`;

    nonDataBearingItems.forEach((item) => {
      csv += `"${item.productType}","${item.jobNo}","${
        item.qty
      }","${item.totalWeight.toFixed(2)}","${item.jobStatus}","${
        item.eolStatus
      }"\n`;
    });

    return csv;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        '@media print': {
          bgcolor: 'white',
          minHeight: 'auto',
          '@page': {
            size: 'A4 landscape',
            margin: '0.5in',
          },
          '& .print-hide': {
            display: 'none !important',
          },
          '& .MuiContainer-root': {
            maxWidth: 'none !important',
            padding: '8px !important',
          },
          '& .MuiPaper-root': {
            boxShadow: 'none !important',
            border: '1px solid #e0e0e0',
          },
          '& .MuiTableCell-root': {
            fontSize: '0.75rem !important',
            padding: '4px 8px !important',
            borderBottom: '1px solid #e0e0e0 !important',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            fontWeight: 'bold !important',
            backgroundColor: '#f5f5f5 !important',
          },
        },
      }}
    >
      {/* Header Banner */}
      <Box
        sx={{
          bgcolor: customerTheme.customer.primaryColor,
          color: 'white',
          py: 4,
          position: 'relative',
          '@media print': {
            bgcolor: 'white !important',
            color: 'black !important',
            py: 2,
            borderBottom: '3px solid #184863',
            boxShadow: 'none',
          },
        }}
      >
        <Container maxWidth='xl'>
          <Grid container alignItems='center' justifyContent='space-between'>
            <Grid item>
              <Typography
                variant='h3'
                component='h1'
                fontWeight='bold'
                sx={{
                  fontFamily: customerTheme.customer.fontFamily,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  '@media print': {
                    fontSize: '2rem',
                    color: customerTheme.customer.primaryColor + ' !important',
                    textShadow: 'none',
                    fontWeight: 'bold',
                  },
                }}
              >
                Customer Asset Breakdown ("CAB")
              </Typography>
            </Grid>
            <Grid item>
              <Box
                component='img'
                src={customerTheme.customer.logo}
                alt='Assettech Logo'
                sx={{
                  height: { xs: 60, md: 80 },
                  width: 'auto',
                  '@media print': {
                    height: 60,
                    maxWidth: 150,
                    filter: 'none',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth='xl' sx={{ py: 4 }}>
        {/* Action Buttons */}
        <Box
          className='print-hide'
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}
        >
          <Button
            variant='outlined'
            onClick={handleDownloadCSV}
            sx={{
              borderColor: customerTheme.customer.primaryColor,
              color: customerTheme.customer.primaryColor,
              '&:hover': {
                borderColor: customerTheme.customer.primaryColor,
                bgcolor: customerTheme.customer.primaryColor,
                color: 'white',
              },
              fontFamily: customerTheme.customer.fontFamily,
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
            }}
          >
            Download CSV
          </Button>
          <Button
            variant='contained'
            onClick={handlePrintToPDF}
            sx={{
              bgcolor: customerTheme.customer.primaryColor,
              '&:hover': {
                bgcolor: customerTheme.customer.primaryColor,
                opacity: 0.9,
              },
              fontFamily: customerTheme.customer.fontFamily,
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
            }}
          >
            Print to PDF
          </Button>
        </Box>

        {/* Data Bearing Items Table */}
        <Paper sx={{ mb: 4 }}>
          <Box sx={{ p: 3 }}>
            <Typography
              variant='h5'
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: customerTheme.customer.primaryColor,
                fontFamily: customerTheme.customer.fontFamily,
              }}
            >
              DATA BEARING ITEMS
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Unit ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Cust. Asset Tag
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Job No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Product Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Make</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Serial No.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CPU</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>RAM</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Storage</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Product Weight (kg)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>DD Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Job Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      EOL Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataBearingItems.map((asset, index) => (
                    <TableRow
                      key={asset.unitNumber}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}
                    >
                      <TableCell>{asset.unitNumber || ''}</TableCell>
                      <TableCell>{asset.assetTag || ''}</TableCell>
                      <TableCell>{pickup?.orderNumber || ''}</TableCell>
                      <TableCell>{asset.deviceType || ''}</TableCell>
                      <TableCell>{asset.manufacturer || ''}</TableCell>
                      <TableCell>{asset.model || ''}</TableCell>
                      <TableCell>{asset.serialNumber || ''}</TableCell>
                      <TableCell>{asset.cpu || ''}</TableCell>
                      <TableCell>{asset.ram || ''}</TableCell>
                      <TableCell>{asset.storage || ''}</TableCell>
                      <TableCell>{asset.condition || ''}</TableCell>
                      <TableCell>{asset.weight || ''}</TableCell>
                      <TableCell>{assetDdStatus(asset)}</TableCell>
                      <TableCell>{pickup?.state || ''}</TableCell>
                      <TableCell>{assetEolStatus(asset)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        {/* Non-Data Bearing Items Table */}
        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography
              variant='h5'
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: customerTheme.customer.primaryColor,
                fontFamily: customerTheme.customer.fontFamily,
              }}
            >
              NON-DATA BEARING ITEMS
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Product Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Job No.</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>QTY</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Total Weight (kg)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Job Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      EOL Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nonDataBearingItems.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}
                    >
                      <TableCell>{item.productType}</TableCell>
                      <TableCell>{item.jobNo}</TableCell>
                      <TableCell>{item.qty}</TableCell>
                      <TableCell>{item.totalWeight.toFixed(2)}</TableCell>
                      <TableCell>{item.jobStatus}</TableCell>
                      <TableCell>{item.eolStatus}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
